import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const STATUS_OPTS = ['placed', 'in_production', 'shipped', 'delivered', 'cancelled'];

export const OrdersSection = () => {
  const { refreshStats } = useOutletContext();
  const { format } = useCurrency();
  const confirm = useConfirm();
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [archivedFilter, setArchivedFilter] = useState('false');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const sel = useSelection(orders);

  const load = (p = page) => {
    const params = { status: statusFilter, archived: archivedFilter, page: p, page_size: 25 };
    if (search) params.search = search;
    api.get('/admin/orders', { params }).then(({ data }) => {
      setOrders(data.items || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      setPage(data.page || p);
      sel.clear();
    });
  };
  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [statusFilter, archivedFilter]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Server does the filtering now; expose orders directly.
  const filtered = orders;

  const bulkRun = async (action, extra = {}) => {
    const body = { ids: sel.selected, action, ...extra };
    try {
      const { data } = await api.post('/admin/orders/bulk', body);
      toast.success(`Applied to ${data.affected} order${data.affected === 1 ? '' : 's'}`);
      load(); refreshStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    }
  };

  const bulkArchive = async (archived) => {
    const ok = await confirm({
      title: archived ? `Archive ${sel.size} order${sel.size === 1 ? '' : 's'}?` : `Restore ${sel.size} order${sel.size === 1 ? '' : 's'}?`,
      description: archived ? 'They will hide from active revenue KPIs. Restore any time.' : 'They will return to the active list and count toward revenue.',
      confirmLabel: archived ? 'Archive selected' : 'Restore selected',
    });
    if (!ok) return;
    bulkRun(archived ? 'archive' : 'unarchive');
  };

  const bulkDelete = async () => {
    const ok = await confirm({
      title: `Delete ${sel.size} order${sel.size === 1 ? '' : 's'} permanently?`,
      description: 'This cannot be undone. Consider archiving instead if unsure.',
      confirmLabel: 'Delete permanently',
      variant: 'danger',
    });
    if (!ok) return;
    bulkRun('delete');
  };

  const updateStatus = async (id, status) => {
    // Moving to `in_production` is the accounting trigger — invoice number
    // gets allocated on the backend. Prompt the operator to add / confirm the
    // shipping cost first so the invoice PDF reflects the final billed amount.
    if (status === 'in_production') {
      const target = filtered.find(x => x.id === id);
      const proposed = window.prompt(
        `Add shipping cost for order ${target?.order_number || ''}\n\nEnter shipping amount (USD, 0 for free), then the invoice will be issued.`,
        String(target?.shipping ?? 0),
      );
      if (proposed === null) return; // cancelled
      const amt = Number(proposed);
      if (!Number.isFinite(amt) || amt < 0) { toast.error('Invalid shipping amount'); return; }
      try {
        await api.patch(`/admin/orders/${id}/shipping-cost?shipping_cost=${amt}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not set shipping cost'); return;
      }
    }
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(status === 'in_production' ? 'Invoice issued & moved to production' : 'Status updated');
      load(); refreshStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const markPaid = async (id) => {
    const note = window.prompt('Add a note for the payment record (e.g. transfer ref, cash, etc.)', '');
    if (note === null) return;
    try {
      await api.patch(`/admin/orders/${id}/payment`, { note });
      toast.success('Marked as paid');
      load(); refreshStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not mark as paid'); }
  };

  const archiveOrder = async (id, archived) => {
    const ok = await confirm({
      title: archived ? 'Archive this order?' : 'Restore this order?',
      description: archived ? 'It will be hidden from the active list but not deleted.' : 'It will return to the active orders list.',
      confirmLabel: archived ? 'Archive' : 'Restore',
    });
    if (!ok) return;
    try {
      await api.patch(`/admin/orders/${id}/archive?archived=${archived}`);
      toast.success(archived ? 'Archived' : 'Restored');
      load(); refreshStats();
    } catch { toast.error('Failed'); }
  };

  const deleteOrder = async (id) => {
    const ok = await confirm({
      title: 'Delete this order permanently?',
      description: 'This cannot be undone. Consider archiving instead if unsure.',
      confirmLabel: 'Delete permanently',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success('Order deleted');
      load(); refreshStats();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-40">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--jw-text-subtle)' }} />
          <Input placeholder="Search order # or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-orders-search-input" className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="admin-orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={archivedFilter} onValueChange={setArchivedFilter}>
          <SelectTrigger className="w-36" data-testid="admin-orders-archived-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="false">Active</SelectItem>
            <SelectItem value="true">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="jw-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={sel.allSelected}
                    ref={(el) => { if (el) el.indeterminate = sel.someSelected; }}
                    onChange={sel.toggleAll}
                    data-testid="admin-orders-select-all"
                    aria-label="Select all orders on this page"
                  />
                </TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Design</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Placed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(o => (
                <TableRow key={o.id} data-testid={`admin-order-${o.id}-row`} data-selected={sel.has(o.id) ? 'true' : 'false'}>
                  <TableCell>
                    <input type="checkbox" checked={sel.has(o.id)} onChange={() => sel.toggle(o.id)} data-testid={`admin-order-${o.id}-select`} aria-label={`Select order ${o.order_number}`} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{o.order_number}</div>
                    {o.invoice_number && (
                      <div className={`text-[10px] mt-0.5 ${o.is_voided ? 'text-rose-300 line-through' : 'text-emerald-300'}`} title={o.invoiced_at}>
                        {o.invoice_number}{o.is_voided ? ' · VOID' : ''}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5" data-testid={`admin-order-${o.id}-design-cell`}>
                      {(o.items || []).slice(0, 3).map((it, idx) => {
                        const src = it.user_design?.preview_url || it.design?.image_url;
                        if (!src) return (
                          <div key={idx} title={it.product?.name || 'Blank'} className="w-9 h-9 rounded bg-white/[0.04] border border-white/10 grid place-items-center">
                            <Palette className="w-3 h-3 opacity-40" />
                          </div>
                        );
                        return (
                          <a
                            key={idx}
                            href={assetUrl(src)}
                            target="_blank"
                            rel="noreferrer"
                            title={`${it.user_design?.name || it.design?.name || 'Design'} — click to download for printing`}
                            data-testid={`admin-order-${o.id}-design-${idx}`}
                            className="w-9 h-9 rounded overflow-hidden bg-white border border-white/10 hover:ring-2 hover:ring-orange-400"
                          >
                            <img src={assetUrl(src)} alt="" className="w-full h-full object-contain" />
                          </a>
                        );
                      })}
                      {(o.items || []).length > 3 && (
                        <span className="text-[10px]" style={{ color: 'var(--jw-text-subtle)' }}>+{(o.items || []).length - 3}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{o.user?.name} <span style={{ color: 'var(--jw-text-subtle)' }}>({o.user?.email})</span></TableCell>
                  <TableCell>
                    <div>{format(o.total)}</div>
                    {o.invoice_number && !o.is_voided && (
                      <div className={`text-[10px] mt-0.5 ${o.paid_at ? 'text-emerald-300' : 'text-amber-300'}`}>
                        {o.paid_at ? 'Paid' : 'Awaiting payment'}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{new Date(o.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger data-testid={`admin-order-${o.id}-status-select`} className="w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {o.invoice_number && !o.is_voided && !o.paid_at && (
                      <Button size="sm" variant="ghost" onClick={() => markPaid(o.id)} title="Mark payment received" data-testid={`admin-order-${o.id}-mark-paid-button`} className="text-emerald-400 h-8">
                        <DollarSign className="w-4 h-4 mr-1" /> Paid
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openInvoice(o.id)} title={o.invoice_number ? 'Download invoice' : 'Invoice not yet issued'} data-testid={`admin-order-${o.id}-invoice-button`} disabled={!o.invoice_number}>
                      <FileDown className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => archiveOrder(o.id, !o.archived)} title={o.archived ? 'Restore' : 'Archive'} data-testid={`admin-order-${o.id}-archive-button`}>
                      {o.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteOrder(o.id)} title="Delete" data-testid={`admin-order-${o.id}-delete-button`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8" style={{ color: 'var(--jw-text-muted)' }}>No orders found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 pb-4">
          <Pagination page={page} pages={pages} total={total} onChange={(p) => { setPage(p); load(p); }} testIdPrefix="admin-orders-pagination" />
        </div>
      </Card>

      <BulkActionBar
        count={sel.size}
        onClear={sel.clear}
        testIdPrefix="admin-orders-bulk"
        actions={[
          ...STATUS_OPTS.map((s) => ({
            key: `status-${s}`,
            label: `Set ${s.replace('_', ' ')}`,
            onClick: () => bulkRun('set_status', { value: s }),
          })),
          { key: 'archive', label: 'Archive', icon: Archive, onClick: () => bulkArchive(true) },
          { key: 'unarchive', label: 'Restore', icon: ArchiveRestore, onClick: () => bulkArchive(false) },
          { key: 'delete', label: 'Delete', icon: Trash2, onClick: bulkDelete, variant: 'danger' },
        ]}
      />
    </div>
  );
};

export default OrdersSection;
